import classNames from "classnames";
import { COMMENT_TYPE } from "store/apis/notes";
import { AmountSuperScript } from "components/design/AmountSuperScript";
import { CategoryLabel } from "components/design/CategoryLabel";
import ConditionalToolTip from "components/design/conditionalToolTip";
import { Avatar } from "components/DesignSystem/AvatarGroup/Avatar";
import { AvatarGroup } from "components/DesignSystem/AvatarGroup/AvatarGroup";
import { Button } from "components/DesignSystem/Button/Button";
import {
  Combobox,
  ComboboxCustomSelectProps,
  OptionData,
} from "components/DesignSystem/Combobox/Combobox";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import { ArrowDown } from "components/icons/ArrowDown";
import { BankLogo } from "components/icons/BankLogo";
import { Cross } from "components/icons/Cross";
import { LinkTransaction } from "components/Transaction/LinkTransaction";
import {
  ChangeCategory,
  Uncategorise,
} from "components/Transaction/UnLinkTransaction";
import { STANDARD } from "constants/bookkeeping";
import { TRANSACTION_SOURCE } from "dictionaries";
import { AnimatePresence, motion } from "framer-motion";
import { useChartOfAccounts } from "hooks/useChartOfAccounts";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useModal } from "hooks/useModal";
import { useToast } from "hooks/useToast";
import { ReactNode, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  ClearIndicatorProps,
  components,
  ControlProps,
  GroupBase,
  Props,
  SingleValue,
} from "react-select";
import { FilterOptionOption } from "react-select/dist/declarations/src/filters";
import { CellContext } from "react-table-8.10.7";
import ArrowElbowDownRight from "static/images/ArrowElbowDownRight.svg";
import CardAccount from "static/images/CardAccount.svg";
import ThreeDots from "static/images/ThreeDots.svg";
import {
  useAssignExistingMerchantMutation,
  useCreateMerchantMutation,
  useDeleteMerchantFromTxnMutation,
} from "store/apis/merchant";
import {
  useAddTransactionInvoiceMutation,
  useGetMappedInvoiceQuery,
  useUpdateTransactionMutation,
} from "store/apis/transactions";
import {
  openSimilarTxnsSnackbar,
  setUpdateTxnResponse,
} from "store/slices/similarTransactions";
import { openPrepaidExpenseModal } from "store/slices/prepaidExpenseModal";
import { openFixedAssetModal } from "store/slices/fixedAssetModal";
import {
  openDeleteSplitTransactionModal,
  openEditSplitTransactionModal,
  openSplitTransactionModal,
} from "store/slices/splitTransaction";
import { Transaction, Transactions, TxnCategories } from "types/Models/books";
import { BackendError } from "types/utils/error";
import { FileIcon } from "components/design/FileIcon";
import { MerchantSelector } from "./MerchantSelector";
import { RemoveSplitActionModal } from "./RemoveSplitActionModal";
import { SplitCategoryOnPopover } from "./SplitCategoryOnPopover";
import { FileInput, FileType } from "components/FileInput/FileInput";
import { ActionDropdown } from "./ActionDropdown";
import * as Popover from "@radix-ui/react-popover";
import { AddCommentPop } from "components/AddComment/AddComment";
import { AddCommentPopoverRoot } from "components/AddCommentPopoverRoot/AddCommentPopoverRoot";
import { InternalTransaction } from "./AddTransaction/AddTransactionManuallyModal";
import { CategoryStatus } from "./CategoryStatus";
import dayjs from "dayjs";
import { DISPLAY_DATE } from "constants/date";
import { useConstructInternalLink } from "hooks/useConstructInternalLink";
import { Link } from "components/DesignSystem/Link/Link";
import ToolTip from "components/design/toolTip";
import { Link as LinkIcon } from "components/icons/Link";
import { pluralize } from "utils/pluralize";
import { Skeleton } from "components/DesignSystem/Skeleton/Skeleton";
import { CategorySelector as CategorySelectorControlled } from "components/CategorySelector/CategorySelector";
import { LinkInvoice } from "components/icons/LinkInvoice";
import { CategorisationStatusInfo } from "./CategorisationStatusInfo";
import { useTransactionActions } from "./useTransactionActions";
import { useHasWritePermission } from "hooks/useHasWritePermission";
import { WRITE_PERMISSION_KEYS } from "constants/permissions";
import { Padlock } from "components/icons/Padlock";
import { AddNewAccount } from "components/ChartOfAccounts/AddNewAccount";
import {
  FIXED_ASSET_CATEGORY_IDENTIFIER_PREFIX,
  PREPAID_EXPENSES_CATEGORY_TAG,
} from "constants/categoryIdentifier";
import { ClockCounterClockwise } from "components/icons/ClockCounterClockwise";
import { CategorisationHistoryModal } from "components/CategorisationHistory/CategorisationHistoryModal";
import { colors } from "constants/colors";

type CategoryOption = {
  value: string;
  label: React.JSX.Element;
  data: string;
  isDisabled: boolean;
  categoryType?: string;
  options?: CategoryOption[];
  indentLevel: number;
  name?: string;
};

const getTextAndRemaining = ({
  mainDetail,
  splitDetails,
  splitCount,
}: {
  mainDetail?: string;
  splitDetails?: string;
  splitCount?: number;
}) => {
  const splitDetailNames = splitDetails?.split(",");
  const nameToShow = mainDetail || splitDetailNames?.[0];
  const extraNumber = mainDetail ? splitCount : (splitCount as number) - 1;
  return { nameToShow, extraNumber };
};

const getMerchantTextAndRemaining = ({
  mainDetail,
  splitDetails,
  splitCount,
}: {
  mainDetail?: string;
  splitDetails?: Transaction[] | null;
  splitCount?: number;
}) => {
  const splitDetailNames = splitDetails?.map(
    (transaction) => transaction.merchant
  );
  const nameToShow = mainDetail || splitDetailNames?.[0];
  const extraNumber = mainDetail ? splitCount : (splitCount as number) - 1;
  return { nameToShow, extraNumber };
};

export const getCOALabel = ({ account }: { account: TxnCategories }) => {
  return account.category_type === STANDARD
    ? account.identifier.toString().concat(" - ").concat(account.name)
    : account.name;
};

export type SourceTransaction = {
  transactionInfo: Pick<
    Transactions["transaction"],
    | "amount"
    | "from"
    | "logo"
    | "merchant"
    | "date"
    | "uuid"
    | "memo"
    | "category"
    | "linked_transaction"
  >;
  category: {
    categoryType: string;
    categoryName: string;
    categoryId: string;
    data: string;
  };
};

export const TransactionComboboxControl = ({
  children,
  ...props
}: ControlProps<OptionData>) => {
  const selectProps = props.selectProps as Props<
    OptionData,
    boolean,
    GroupBase<OptionData>
  > &
    ComboboxCustomSelectProps;

  const { value } = selectProps;

  return (
    <components.Control
      {...props}
      className={classNames(
        "t-px-2 t-border t-border-solid hover:t-border-neutral-10 hover:t-bg-surface-grey t-rounded !t-cursor-pointer t-flex t-items-center",
        {
          " t-border-neutral-0 t-bg-surface-lighter-grey": Boolean(!value),
          "t-border-surface-transparent": Boolean(value),
          "!t-min-h-7": selectProps.size === "xsmall",
          "!t-min-h-8": selectProps.size === "small",
          "!t-min-h-10": selectProps.size === "regular",
          "!t-min-h-12": selectProps.size === "large",
        }
      )}
    >
      {children}
    </components.Control>
  );
};

export const DropdownIndicator = () => {
  return (
    <div className="group-data-state-open:t-rotate-180 group-hover:t-opacity-100 t-opacity-0 group-data-state-open:t-opacity-100 t-flex t-items-center t-w-0 group-hover:t-w-auto">
      <ArrowDown color="currentColor" />
    </div>
  );
};

export const TransactionComboboxClearIndicator = (
  props: ClearIndicatorProps<OptionData>
) => {
  return (
    <components.ClearIndicator {...props} className="!t-p-0">
      <button
        type="button"
        className="all:unset t-mr-1 group-hover:t-opacity-100 t-opacity-0 t-w-0 group-hover:t-w-auto t-flex t-items-center"
        tabIndex={-1}
      >
        <Cross color="currentColor" size="10" />
      </button>
    </components.ClearIndicator>
  );
};

const AttachInvoice = ({ transactionId }: { transactionId: string }) => {
  const { successToast, alertToast } = useToast();
  const { uuid: groupId } = useCurrentGroupContext();
  const [addInvoice, { isLoading: isUploading }] =
    useAddTransactionInvoiceMutation();

  const onDrop = async (files: FileType[]) => {
    try {
      await addInvoice({
        groupId,
        previewTxnId: transactionId,
        files,
      }).unwrap();
      successToast({ message: "Invoice added" });
    } catch (error) {
      alertToast({ message: (error as BackendError)?.data?.error?.message });
    }
  };

  return (
    <FileInput
      onDrop={onDrop}
      disabled={isUploading}
      isUploading={isUploading}
      variant="icon"
    />
  );
};

const SourceLogo = ({
  from,
  is_credit_card,
}: {
  from: Transaction["from"];
  is_credit_card: Transaction["is_credit_card"];
}) => {
  const [hover, setHover] = useState(false);
  const { bank_account } = from || {};
  const { bank_brand } = bank_account || {};
  const { logo_url } = bank_brand || {};

  const mask = from?.bank_account?.mask
    ? `•••• ${from?.bank_account?.mask}`
    : "";
  return (
    <ConditionalToolTip
      condition={
        from &&
        `${from?.bank_account?.nickname} ${mask} (${
          TRANSACTION_SOURCE[from?.source!]
        })`
      }
    >
      <motion.span
        className="t-flex t-justify-center"
        whileHover={
          is_credit_card
            ? {
                transition: {
                  duration: 0.5,
                  ease: "easeInOut",
                },
                rotateY: 180,
              }
            : {}
        }
        onHoverStart={() => setHover(true)}
        onHoverEnd={() => setHover(false)}
      >
        {!hover && is_credit_card ? (
          <Avatar src={CardAccount} alt="Bank" size="extra-small" />
        ) : (
          <AnimatePresence>
            <motion.span
              animate={
                hover && is_credit_card
                  ? {
                      transition: {
                        duration: 0.5,
                        ease: "easeInOut",
                      },
                      rotateY: 180,
                    }
                  : {}
              }
            >
              {logo_url ? (
                <Avatar
                  src={logo_url}
                  alt={from?.bank_account?.mask || "Bank"}
                  size="extra-small"
                />
              ) : (
                <BankLogo />
              )}
            </motion.span>
          </AnimatePresence>
        )}
      </motion.span>
    </ConditionalToolTip>
  );
};
const Source = ({ transaction }: { transaction: Transaction }) => {
  return (
    <SourceLogo
      from={transaction.from}
      is_credit_card={transaction.is_credit_card}
    />
  );
};

export const MerchantSelectDelete = ({
  transactions,
  outline,
  splitTransactionsMerchantCount,
  splitTransactionVendors,
  showSplitVendors,
  disabled,
  showCategorisationHistoryAlways = false,
}: {
  transactions: Transactions;
  outline?: boolean;
  onlyLogo?: boolean;
  splitTransactionsMerchantCount?: number;
  splitTransactionVendors?: Transaction[] | null;
  showSplitVendors?: boolean;
  disabled?: boolean;
  showCategorisationHistoryAlways?: boolean;
}) => {
  const { transaction } = transactions;
  const { successToast, alertToast } = useToast();
  const { uuid: groupId } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();
  const dispatch = useDispatch();
  const removeSplitTxns = useModal();
  const [merchantIdToAssignTransaction, setMerchantIdToAssignTransaction] =
    useState("");

  const [assignExistingMerchantToTransaction, { isLoading: isAssiging }] =
    useAssignExistingMerchantMutation();

  const [createMerchant, { isLoading: isCreatingMerchant }] =
    useCreateMerchantMutation();

  const [deleteMerchant, { isLoading: isDeleting }] =
    useDeleteMerchantFromTxnMutation();

  const { logo, merchant, uuid: transactionId } = transaction;

  const showSplitMerchantCount = Boolean(
    splitTransactionsMerchantCount &&
      splitTransactionsMerchantCount > 0 &&
      !transactions.has_linked_invoices
  );

  const isSplitTransaction =
    transactions?.split_transactions &&
    transactions.split_transactions.length > 0 &&
    !transactions.has_linked_invoices;

  const openRemoveSplitTransaction = ({
    selectedMerchantId,
  }: {
    selectedMerchantId?: string;
  }) => {
    if (!selectedMerchantId) {
      dispatch(openDeleteSplitTransactionModal(transactions));
      return;
    }

    setMerchantIdToAssignTransaction(selectedMerchantId);
    removeSplitTxns.open();
  };

  const handleMerchantChange = async (values: SingleValue<any>) => {
    if (isSplitTransaction) {
      openRemoveSplitTransaction({ selectedMerchantId: values?.merchantId });
      return;
    } else {
      await merchantChange({ merchantId: values?.merchantId });
    }
  };

  const merchantChange = async ({ merchantId }: { merchantId?: string }) => {
    if (merchantId) {
      const payload = { merchant_id: merchantId };
      try {
        await assignExistingMerchantToTransaction({
          groupId,
          transactionId,
          payload,
        }).unwrap();
        successToast({ message: "Vendor assigned" });
      } catch (error) {
        alertToast({
          message: (error as BackendError)?.data?.error?.message,
        });
      }
    } else {
      try {
        await deleteMerchant({ groupId, transactionId }).unwrap();
        successToast({ message: "Vendor unassigned" });
      } catch (error) {
        alertToast({
          message: (error as BackendError)?.data?.error?.message,
        });
      }
    }
  };

  const onCreateMerchant = async (name: string) => {
    try {
      const formData = new FormData();
      formData.append("name", name);
      formData.append("txn_uuid", transactionId);

      await createMerchant({
        groupId,
        entityId,
        payload: formData,
      }).unwrap();
    } catch (error) {
      alertToast({ message: (error as BackendError)?.data?.error?.message });
    }
  };

  const closeRemoveSplitTxns = () => {
    removeSplitTxns.close();
    setMerchantIdToAssignTransaction("");
  };

  const { nameToShow, extraNumber } = getMerchantTextAndRemaining({
    mainDetail: merchant,
    splitDetails: splitTransactionVendors || [],
    splitCount: splitTransactionsMerchantCount,
  });

  const value =
    merchant || nameToShow
      ? {
          value: merchant || "",
          label: (
            <div className="t-flex t-gap-2 t-items-center">
              <Avatar
                src={logo || ""}
                alt={merchant || (nameToShow as string)}
              />
              <div className="t-text-body-sm t-text-text-60 t-max-w-[300px] t-truncate">
                {nameToShow}
              </div>
              {showSplitMerchantCount && (
                <div className="t-text-subtext-sm t-text-purple">
                  +{extraNumber}
                </div>
              )}
            </div>
          ),
        }
      : null;

  return (
    <div
      className="t-group t-select-none t-flex t-items-center t-gap-0.5 t-w-full"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="t-w-full">
        <MerchantSelector
          isDisabled={disabled}
          customComponent={!outline}
          size="small"
          onCreateMerchant={onCreateMerchant}
          merchantChange={handleMerchantChange}
          key={transactionId}
          isLoading={isAssiging || isDeleting || isCreatingMerchant}
          defaultValue={value}
          value={value}
          selected={merchant}
          creatable={true}
          showMerchantDetails={Boolean(showSplitVendors && isSplitTransaction)}
          transactions={transactions}
        />
      </div>

      {!(
        transactions?.split_transactions &&
        transactions.split_transactions.length > 0
      ) &&
        !transactions.has_linked_invoices && (
          <MerchantCategorisationHistory
            transactions={transactions}
            showAlways={showCategorisationHistoryAlways}
          />
        )}
      <RemoveSplitActionModal
        isOpen={removeSplitTxns.isOpen}
        close={closeRemoveSplitTxns}
        transaction={transactions}
        onMerchantConfirm={merchantChange}
        merchantIdToAssignTransaction={merchantIdToAssignTransaction}
      />
    </div>
  );
};

const Merchant = ({
  info,
  showSplitVendors,
  disabled,
}: {
  info: CellContext<Transactions, Transaction["merchant"]>;
  showSplitVendors?: boolean;
  disabled?: boolean;
}) => {
  const hasWritePermission = useHasWritePermission(
    WRITE_PERMISSION_KEYS.TRANSACTIONS
  );
  const effectiveDisabled = disabled || !hasWritePermission;

  const {
    row: {
      original: { split_transactions_merchant_count },
    },
  } = info;
  const splitTransactionVendors = info.row.original.split_transactions?.reduce(
    (acc, transaction) => {
      if (transaction.merchant) {
        if (acc.length > 0) {
          acc += ", ";
        }
        acc += transaction.merchant;
      }
      return acc;
    },
    ""
  );

  return (
    <MerchantSelectDelete
      disabled={effectiveDisabled}
      transactions={info.row.original}
      onlyLogo={true}
      splitTransactionsMerchantCount={split_transactions_merchant_count}
      splitTransactionVendors={info.row.original.split_transactions}
      showSplitVendors={Boolean(showSplitVendors && splitTransactionVendors)}
    />
  );
};

export const MerchantCategorisationHistory = ({
  transactions,
  showAlways = false,
}: {
  transactions: Transactions;
  showAlways?: boolean;
}) => {
  const hasWritePermission = useHasWritePermission(
    WRITE_PERMISSION_KEYS.TRANSACTIONS
  );
  const categorisationHistoryModal = useModal();
  const { successToast, alertToast } = useToast();
  const { uuid: groupId } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();

  const [updateTransaction, { isLoading: isAssigning }] =
    useUpdateTransactionMutation();

  const { transaction } = transactions;
  const { merchant_uuid, merchant, uuid: transactionId } = transaction;

  const handleCategoryAssign = async (categoryId: string) => {
    try {
      await updateTransaction({
        groupId,
        entityId,
        previewTxnId: transactionId,
        payload: {
          transaction_category_uuid: categoryId,
        },
      }).unwrap();
      successToast({ message: "Category assigned successfully" });
      categorisationHistoryModal.close();
    } catch (error) {
      alertToast({
        message: (error as BackendError)?.data?.error?.message,
      });
    }
  };

  if (!merchant_uuid) {
    return null;
  }

  return (
    <>
      <ToolTip text="Categorization history">
        <span
          className={classNames({
            "t-opacity-0 group-hover:t-opacity-100 t-transition-opacity":
              !showAlways,
          })}
        >
          <Button
            customType="ghost_icon"
            size="xsmall"
            onClick={(e) => {
              e.stopPropagation();
              categorisationHistoryModal.open();
            }}
          >
            <ClockCounterClockwise color={colors.text[30]} size={16} />
          </Button>
        </span>
      </ToolTip>
      {categorisationHistoryModal.isOpen && (
        <CategorisationHistoryModal
          isOpen={categorisationHistoryModal.isOpen}
          onClose={categorisationHistoryModal.close}
          merchantId={merchant_uuid}
          vendorName={merchant}
          onCategoryAssign={handleCategoryAssign}
          isAssigning={isAssigning}
          readOnly={!hasWritePermission}
        />
      )}
    </>
  );
};

const CategoryChangeWrapper = ({
  children,
  transaction,
}: {
  children: ({
    onChange,
    isCategorising,
  }: {
    onChange: ({ value }: any) => void;
    isCategorising: boolean;
  }) => ReactNode;
  transaction: Transactions;
}) => {
  const [sourceTransaction, setSourceTransaction] =
    useState<SourceTransaction | null>();

  const { successToast, alertToast } = useToast();
  const { uuid: groupId } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();
  const dispatch = useDispatch();

  const [updateTransaction, { isLoading: isUpdating }] =
    useUpdateTransactionMutation();

  const useCOA = () => {
    const { chartOfAccounts = [] } = useChartOfAccounts({
      hiddenCategoryTypes: ["BANK_ACCOUNT"],
    });

    return coaOptions(chartOfAccounts);
  };
  const flattenChartOfAccounts = useCOA();

  const { uuid, memo, category, linked_transaction } = transaction.transaction;

  const assignCategoryToTxn = async ({
    transactionCategoryId,
    previewTxnId,
  }: {
    transactionCategoryId?: string | null;
    previewTxnId: string;
  }) => {
    try {
      const transactionResponse = await updateTransaction({
        groupId,
        entityId,
        previewTxnId,
        payload: {
          linked_transaction_uuid: linked_transaction || null,
          transaction_category_uuid: transactionCategoryId || null,
          memo: memo || null,
        },
      }).unwrap();

      const isNegativeTransaction = transaction.transaction.amount < 0;

      const prepaidExpenseCategory = flattenChartOfAccounts.find(
        (coa) => coa.name === PREPAID_EXPENSES_CATEGORY_TAG
      );
      const isPrepaidExpenseCategory =
        prepaidExpenseCategory?.value ===
        transactionResponse?.transaction?.category?.uuid;

      const assignedCategory = transactionResponse?.transaction?.category;
      const isFixedAssetCategory = assignedCategory?.identifier
        ?.toString()
        .startsWith(FIXED_ASSET_CATEGORY_IDENTIFIER_PREFIX);
      if (transactionResponse?.transaction?.category) {
        if (isNegativeTransaction && isPrepaidExpenseCategory) {
          dispatch(openPrepaidExpenseModal(previewTxnId));
          return;
        }

        if (isNegativeTransaction && isFixedAssetCategory) {
          dispatch(
            openFixedAssetModal({
              transactionId: previewTxnId,
              description: transaction.transaction.description || "",
              amount: transaction.transaction.amount,
              date: transaction.transaction.date || "",
            })
          );
          return;
        }

        successToast({ message: "Category assigned" });

        if (
          !isNegativeTransaction &&
          transactionResponse?.group_categorization
            ?.similar_merchant_txns_count! > 0
        ) {
          dispatch(setUpdateTxnResponse(transactionResponse));
          dispatch(openSimilarTxnsSnackbar());
        }
      } else {
        successToast({ message: "Category removed" });
      }
    } catch (error) {
      alertToast({ message: (error as BackendError)?.data?.error?.message });
    }
  };

  const {
    isOpen: isLinkTransactionOpen,
    close: closeLinkTransaction,
    open: openLinkTransaction,
  } = useModal();

  const {
    isOpen: isOpenChangeCategoryModal,
    open: openChangeCategoryModal,
    close: closeChangeCategoryModal,
  } = useModal();

  const {
    isOpen: isOpenUncategoriseModal,
    open: openUncategoriseModal,
    close: closeUncategoriseModal,
  } = useModal();

  const onChange = (option?: {
    label: string;
    value: string;
    categoryType: string;
    data: string;
  }) => {
    if (!option) {
      switch (category?.name) {
        case "Bank Transfer":
          openUncategoriseModal();
          break;
        case "Pay Down Credit":
          openUncategoriseModal();
          break;
        default:
          assignCategoryToTxn({
            transactionCategoryId: "",
            previewTxnId: uuid,
          });
      }
    } else {
      const { categoryType, label, value, data } = option;

      const suggestedCategoryCheck: Boolean =
        categoryType === "SUGGESTED" &&
        (data.includes("Bank Transfer") || data.includes("Pay Down Credit"));

      if (
        category?.name === "Bank Transfer" ||
        category?.name === "Pay Down Credit"
      ) {
        openChangeCategoryModal();
      } else if (
        categoryType === "BANK_TRANSFER" ||
        categoryType === "PAY_DOWN_CREDIT" ||
        suggestedCategoryCheck
      ) {
        openLinkTransaction();
      } else {
        assignCategoryToTxn({
          transactionCategoryId:
            option instanceof Array ? option[0].value : option?.value,
          previewTxnId: uuid,
        });
      }

      if (
        category?.name === "Bank Transfer" ||
        category?.name === "Pay Down Credit" ||
        categoryType === "BANK_TRANSFER" ||
        categoryType === "PAY_DOWN_CREDIT" ||
        suggestedCategoryCheck
      ) {
        setSourceTransaction({
          transactionInfo: transaction.transaction,
          category: {
            categoryName: label,
            categoryType: categoryType,
            categoryId: value,
            data: data.replace(/[0-9]/g, ""),
          },
        });
      }
    }
  };

  const onLinkTransaction = async (
    linkedTransactions: Record<string, InternalTransaction>
  ) => {
    if (sourceTransaction) {
      try {
        await updateTransaction({
          groupId,
          entityId,
          previewTxnId: sourceTransaction?.transactionInfo.uuid!,
          payload: {
            transaction_category_uuid:
              sourceTransaction?.category.categoryId || null,
            memo: sourceTransaction?.transactionInfo.memo || "",
            linked_transaction_uuid:
              linkedTransactions[sourceTransaction.transactionInfo.uuid].uuid ||
              "",
          },
        }).unwrap();
        closeLinkTransaction();
        successToast({ title: "Transaction Linked" });
      } catch (error) {
        alertToast({ message: (error as BackendError)?.data?.error?.message });
        throw error;
      }
    }
  };

  const closeLinkTransactionCallback = () => {
    closeLinkTransaction();
    setSourceTransaction(null);
  };

  const onUnLink = async () => {
    const isLinkTransactionType =
      sourceTransaction?.category.categoryType === "BANK_TRANSFER" ||
      sourceTransaction?.category.categoryType === "PAY_DOWN_CREDIT";

    try {
      await updateTransaction({
        groupId,
        entityId,
        previewTxnId: sourceTransaction?.transactionInfo?.uuid!,
        payload: {
          transaction_category_uuid: isLinkTransactionType
            ? ""
            : sourceTransaction?.category?.categoryId || "",
          memo: sourceTransaction?.transactionInfo?.memo || "",
          linked_transaction_uuid: "",
        },
      }).unwrap();
      closeChangeCategoryModal();
      if (isLinkTransactionType) openLinkTransaction();
      successToast({ title: "Transaction Unlinked" });
    } catch (error) {
      alertToast({ message: (error as BackendError)?.data?.error?.message });
    }
  };

  return (
    <>
      {children({ onChange, isCategorising: isUpdating })}
      {isLinkTransactionOpen && (
        <LinkTransaction
          onSelect={onLinkTransaction}
          isLinkTransactionOpen={isLinkTransactionOpen}
          closeLinkTransaction={closeLinkTransactionCallback}
          sourceTransaction={sourceTransaction!}
          isUpdating={isUpdating}
        />
      )}
      {isOpenChangeCategoryModal && (
        <ChangeCategory
          isOpen={isOpenChangeCategoryModal}
          close={closeChangeCategoryModal}
          onConfirm={onUnLink}
          isUpdating={isUpdating}
          categoryName={
            sourceTransaction?.transactionInfo?.category?.name ?? ""
          }
        />
      )}
      {isOpenUncategoriseModal && (
        <Uncategorise
          isOpen={isOpenUncategoriseModal}
          close={closeUncategoriseModal}
          transactionDetail={transaction}
        />
      )}
    </>
  );
};

export const CategoryIndent = ({
  label,
  indentLevel,
}: {
  label: ReactNode;
  indentLevel: number;
}) => {
  const paddingValue = indentLevel * 16;
  return (
    <div
      style={{ paddingLeft: indentLevel === 0 ? "0px" : `${paddingValue}px` }}
      className="t-flex t-gap-1.5 t-w-full"
    >
      {indentLevel !== 0 && (
        <img src={ArrowElbowDownRight} alt="ArrowElbowDownRight" />
      )}
      {label}
    </div>
  );
};

export const coaOptions = (
  chartOfAccounts: TxnCategories[],
  indentLevel: number = 0,
  topLevel: boolean = true,
  hiddenCategories?: string[]
) => {
  let options: CategoryOption[] = [];

  chartOfAccounts.forEach((category) => {
    const label = (
      <CategoryIndent
        label={<CategoryLabel category={category} indentLevel={indentLevel} />}
        indentLevel={indentLevel}
      />
    );

    const disabled = Boolean(
      topLevel &&
        category.category_type !== "BANK_TRANSFER" &&
        category.category_type !== "PAY_DOWN_CREDIT" &&
        category.category_type !== "SUGGESTED"
    );

    const option: CategoryOption = {
      value: category.uuid,
      label,
      data: category.identifier.toString().concat(category.name),
      isDisabled: disabled,
      categoryType: category.category_type,
      indentLevel,
      name: category.name,
    };

    options = [...options, option];

    if (category.types && category.types.length > 0) {
      const nestedOptions = coaOptions(category.types, indentLevel + 1, false);
      options = [...options, ...nestedOptions];
    }
  });

  const filteredOptions =
    Array.isArray(hiddenCategories) && hiddenCategories?.length > 0
      ? options.filter(({ value }) => !hiddenCategories?.includes(value))
      : options;

  return filteredOptions;
};

export const coaList = (
  chartOfAccounts: TxnCategories[],
  indentLevel: number = 0,
  topLevel: boolean = true
) => {
  let lists: TxnCategories[] = [];

  chartOfAccounts.forEach((category) => {
    lists = [...lists, category];

    if (category.types && category.types.length > 0) {
      const nestedLists = coaList(category.types, indentLevel + 1, false);
      lists = [...lists, ...nestedLists];
    }
  });

  return lists;
};

const searchTermExistsInChildren = (
  category: TxnCategories,
  searchTerm: string
): boolean => {
  if (category.name.toLowerCase().includes(searchTerm)) {
    return true;
  }

  if (category.types && category.types.length > 0) {
    for (const child of category.types) {
      if (searchTermExistsInChildren(child, searchTerm)) {
        return true;
      }
    }
  }

  return false;
};

export const filterOption = (
  option: FilterOptionOption<OptionData>,
  searchedTerm: string,
  chartOfAccounts: TxnCategories[]
) => {
  if (!option.data?.data) {
    return true;
  }

  const optionData = option.data as CategoryOption;

  const searchTerm = searchedTerm.toLowerCase();
  const categoryData = optionData?.data.toLowerCase();
  const indentLevel = optionData.indentLevel;
  let includeOption = false;

  if (categoryData.includes(searchTerm)) {
    includeOption = true;
  }

  if (indentLevel === 0) {
    const categoryId = optionData.value;
    const category = chartOfAccounts.find(({ uuid }) => uuid === categoryId);

    if (category && searchTermExistsInChildren(category, searchTerm)) {
      includeOption = true;
    }
  }

  return includeOption;
};

const SplitActionButtons = ({ transaction }: { transaction: Transactions }) => {
  const dispatch = useDispatch();

  if (transaction.split_transactions) {
    return (
      <Button
        customType="link"
        onClick={() => dispatch(openEditSplitTransactionModal(transaction))}
      >
        Edit split transaction
      </Button>
    );
  }

  return (
    <Button
      customType="link"
      onClick={() => dispatch(openSplitTransactionModal(transaction))}
    >
      Split transaction
    </Button>
  );
};

export const CategorySelector = ({
  transaction,
  outline,
  truncate,
  showSplitActions,
  txnsToShowInLabel,
  tooltip = false,
  tooltipText,
  isDisabled = false,
  showCategorisationIcon = true,
}: {
  transaction: Transactions;
  outline?: boolean;
  truncate?: boolean;
  showSplitActions?: boolean;
  txnsToShowInLabel: string;
  tooltipText?: string;
  tooltip?: boolean;
  isDisabled?: boolean;
  showCategorisationIcon?: boolean;
}) => {
  const { chartOfAccounts = [] } = useChartOfAccounts({
    hiddenCategoryTypes: ["BANK_ACCOUNT"],
  });
  const { isOpen, close, open } = useModal();
  const dispatch = useDispatch();
  const removeSplitTxns = useModal();
  const [selectedCategoryDetail, setSelectedCategoryDetail] = useState();
  const { uuid: groupId } = useCurrentGroupContext();
  const currentEntityId = useCurrentEntityId();
  const [categoryName, setCategoryName] = useState<string>("");

  const {
    transaction: { categorisation_status, uuid, category },
  } = transaction;

  const filteredSuggestedCategories =
    transaction.transaction.suggested_categories.filter(
      ({ uuid }) => category?.uuid !== uuid
    );

  const isSplitTransaction =
    transaction.split_transactions && transaction.split_transactions.length > 0;

  const totalAssignedCategory = transaction.split_transactions
    ? transaction.split_transactions.length
    : 0;

  const hasMultipleCategory = totalAssignedCategory !== 0;
  const notCategorised = categorisation_status === "NOT_CATEGORISED";

  const suggestedCategories: TxnCategories[] = filteredSuggestedCategories.map(
    ({ identifier, name, uuid }) => ({
      identifier,
      name,
      uuid,
      description: "",
      identifier_prefix: 1,
      parent_id: "",
      category_type: "SUGGESTED",
      parent_name: "",
      start_date: null,
      opening_balance: null,
      manually_added: false,
    })
  );

  const suggestedCategoriesWithParentLabel: TxnCategories[] =
    !Boolean(category?.name || txnsToShowInLabel) &&
    transaction.transaction.suggested_categories.length > 0
      ? [
          {
            uuid: "",
            name: "Suggested",
            description: "",
            identifier: 0,
            identifier_prefix: 1,
            parent_id: "",
            category_type: "SUGGESTED_PARENT",
            parent_name: "",
            start_date: null,
            opening_balance: null,
            manually_added: false,
          },
          ...suggestedCategories,
        ]
      : [];

  const openRemoveSplitTxns = (selectedCategory: any) => {
    if (!selectedCategory?.value) {
      dispatch(openDeleteSplitTransactionModal(transaction));
      return;
    }

    setSelectedCategoryDetail(selectedCategory);
    removeSplitTxns.open();
  };

  const { nameToShow, extraNumber } = getTextAndRemaining({
    mainDetail: category?.name,
    splitDetails: txnsToShowInLabel,
    splitCount: totalAssignedCategory,
  });

  const onCreation = async (name: string) => {
    setCategoryName(name);
  };

  const onParentClose = () => {
    setCategoryName("");
    close();
  };

  return (
    <div
      className="t-group t-select-none"
      onClick={(e) => e.stopPropagation()}
      key={uuid}
    >
      <CategoryChangeWrapper transaction={transaction}>
        {({ onChange, isCategorising }) => (
          <>
            <div className="t-flex t-justify-between t-items-center t-gap-0.5 t-w-full">
              <ConditionalToolTip
                condition={tooltip && tooltipText}
                propClass="t-max-w-96 t-word-break"
              >
                <div
                  className={classNames("t-min-w-0", {
                    "t-flex-1": !notCategorised,
                    "t-w-full": notCategorised,
                  })}
                >
                  <Combobox
                    creatable
                    onCreateOption={onCreation}
                    components={
                      outline
                        ? {}
                        : {
                            Control: TransactionComboboxControl,
                            DropdownIndicator,
                            ClearIndicator: TransactionComboboxClearIndicator,
                          }
                    }
                    isLoading={isCategorising}
                    isDisabled={isDisabled}
                    size="small"
                    placeholder="Select category"
                    menuPlacement="auto"
                    options={coaOptions([
                      ...suggestedCategoriesWithParentLabel,
                      ...chartOfAccounts,
                    ])}
                    // @ts-ignore // Somehow complier is throwing error and vscode is not, check the version and source for both later
                    onChange={
                      isSplitTransaction && showSplitActions
                        ? openRemoveSplitTxns
                        : onChange
                    }
                    filterOption={(option, searchedTerm) =>
                      filterOption(option, searchedTerm, chartOfAccounts)
                    }
                    {...{
                      value:
                        category?.name || txnsToShowInLabel
                          ? {
                              label: (
                                <div className="t-flex t-gap-0.5 t-text-body-sm t-text-text-60">
                                  <div
                                    className={classNames({
                                      "t-truncate t-max-w-fit":
                                        truncate && !hasMultipleCategory,
                                      "t-truncate t-max-w-[130px]":
                                        truncate && hasMultipleCategory,
                                    })}
                                  >
                                    {nameToShow || "-"}
                                  </div>
                                  {hasMultipleCategory && (
                                    <div className="t-text-subtext-sm t-text-purple">
                                      +{extraNumber}
                                    </div>
                                  )}
                                </div>
                              ),
                              value: category?.uuid || "",
                            }
                          : null,
                    }}
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (base) => ({
                        ...base,
                        width: 400,
                      }),
                    }}
                    actions={
                      showSplitActions ? (
                        <SplitActionButtons transaction={transaction} />
                      ) : null
                    }
                  />
                </div>
              </ConditionalToolTip>
              {!notCategorised && showCategorisationIcon && (
                <div className="t-shrink-0 t-flex t-justify-end">
                  <CategoryStatus
                    entityId={currentEntityId}
                    groupId={groupId}
                    transaction={transaction}
                    disabled={isDisabled}
                  />
                </div>
              )}
            </div>
            {isOpen ||
              (Boolean(categoryName) && (
                <AddNewAccount
                  categoryName={categoryName}
                  isOpen={isOpen || Boolean(categoryName)}
                  close={onParentClose}
                  open={open}
                  onSuccess={({ name, uuid, category_type }) =>
                    onChange({
                      label: name,
                      value: uuid,
                      categoryType: category_type,
                    })
                  }
                />
              ))}
            <RemoveSplitActionModal
              isOpen={removeSplitTxns.isOpen}
              close={removeSplitTxns.close}
              transaction={transaction}
              onConfirm={() => onChange(selectedCategoryDetail)}
            />
          </>
        )}
      </CategoryChangeWrapper>
    </div>
  );
};

export const Invoices = ({
  entityId,
  transactionId,
}: {
  entityId: string;
  transactionId: string;
}) => {
  const { link } = useConstructInternalLink();
  const categorisationStatusInfoModal = useModal();

  const { data: linkedInvoices, isLoading: isLinkedTxnDataLoading } =
    useGetMappedInvoiceQuery(
      {
        entityId,
        transactionId: transactionId,
      },
      { skip: !entityId || !transactionId }
    );

  const unrealizedTransactionId = linkedInvoices?.partial_payment.uuid;
  const unrealizedCategoryId = linkedInvoices?.partial_payment.category?.uuid;

  const groupId = useCurrentGroupContext().uuid;
  const { successToast, alertToast } = useToast();

  const [updateTransaction] = useUpdateTransactionMutation();

  const assignCategoryToTxn = async ({
    transactionCategoryId,
    previewTxnId,
  }: {
    transactionCategoryId?: string | null;
    previewTxnId: string;
  }) => {
    try {
      const transactionResponse = await updateTransaction({
        groupId,
        entityId,
        previewTxnId,
        payload: {
          transaction_category_uuid: transactionCategoryId || null,
        },
      }).unwrap();

      if (transactionResponse?.transaction?.category) {
        successToast({ message: "Category assigned" });
      } else {
        successToast({ message: "Category removed" });
      }
    } catch (error) {
      alertToast({ message: (error as BackendError)?.data?.error?.message });
    }
  };

  if (isLinkedTxnDataLoading) {
    return <Skeleton className="t-h-6 t-w-20" />;
  }

  if (!linkedInvoices?.invoices || linkedInvoices.invoices.length === 0) {
    return null;
  }

  const invoices = linkedInvoices.invoices;

  const isBill = invoices?.[0].financial_record_type === "BILL";

  const onChange = (categoryId: string | null) => {
    assignCategoryToTxn({
      transactionCategoryId: categoryId,
      previewTxnId: unrealizedTransactionId!,
    });
  };

  return (
    <div className="t-flex t-flex-col t-gap-1">
      {unrealizedCategoryId && (
        <CategorySelectorControlled
          isClearable={false}
          outline
          category={unrealizedCategoryId}
          withForm={false}
          name="unrealized_income"
          onChange={(value) => onChange(value?.value || null)}
        />
      )}
      <div className="t-flex t-items-center t-justify-between t-gap-0.5 t-w-full">
        <ToolTip
          side="top"
          text={
            invoices.length > 1 ? (
              <div className="t-flex t-flex-col t-min-w-[250px]">
                {invoices.map((invoice) => (
                  <Link
                    key={invoice.uuid}
                    to={link(
                      invoice.financial_record_type === "BILL"
                        ? `/books/bills-and-vendors/bill/${invoice.uuid}`
                        : `/books/invoices-and-customers/${invoice.uuid}`
                    )}
                    className="t-text-white t-truncate t-flex t-justify-between hover:!t-text-neutral-55 t-text-body-sm"
                  >
                    <span>{invoice.invoice_number}</span>
                    <span>
                      <AmountSuperScript
                        amount={Number(invoice.due_balance || 0)}
                      />
                    </span>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="t-w-[250px] t-text-left">
                <div className="t-mb-1">
                  {isBill ? "Bill" : "Invoice"} Details
                </div>
                <div className="t-flex t-flex-col t-gap-1.5">
                  {invoices.at(0)?.invoice_items.map((item) => (
                    <div
                      key={item.uuid}
                      className="t-text-body-sm t-flex t-justify-between"
                    >
                      <div>
                        <div>{item.description}</div>
                        <div className="t-text-neutral-30">
                          {item.category?.name}
                        </div>
                      </div>
                      <span>
                        <AmountSuperScript amount={Number(item.amount || 0)} />
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )
          }
        >
          <div className="t-text-body-sm t-text-text-60 t-flex t-flex-col t-w-[90%] t-px-2">
            <span className="t-text-text-30 t-text-caption">Linked to </span>
            <div>
              <Link
                to={link(
                  isBill
                    ? `/books/bills-and-vendors/bill/${invoices.at(0)?.uuid}`
                    : `/books/invoices-and-customers/${invoices.at(0)?.uuid}`
                )}
              >
                {invoices.at(0)?.invoice_number}{" "}
              </Link>
              {invoices.length > 1 && (
                <span className="t-text-body-sm">+{invoices.length - 1}</span>
              )}
            </div>
          </div>
        </ToolTip>

        <ToolTip
          side="right"
          text={
            <div className="t-items-start t-flex t-flex-col t-text-start">
              Linked to Inkle{" "}
              {isBill
                ? pluralize(invoices.length, "Bill", "Bills", false)
                : pluralize(invoices.length, "Invoice", "Invoices", false)}
              <br />
              <br />
              <button
                className="all:unset t-text-body-sm t-text-white t-underline"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  categorisationStatusInfoModal.open();
                }}
              >
                Tell me more
              </button>
            </div>
          }
        >
          <div>
            <LinkInvoice />
          </div>
        </ToolTip>
      </div>
      <div onClick={(e) => e.stopPropagation()}>
        <CategorisationStatusInfo {...categorisationStatusInfoModal} />
      </div>
    </div>
  );
};

const Category = ({
  info,
  truncate,
  showSplitActions,
  isDisabled = false,
}: {
  info: CellContext<Transactions, Transaction["category"]>;
  truncate?: boolean;
  tooltip?: boolean;
  showSplitActions?: boolean;
  isDisabled?: boolean;
}) => {
  const hasWritePermission = useHasWritePermission(
    WRITE_PERMISSION_KEYS.TRANSACTIONS
  );
  const effectiveIsDisabled = isDisabled || !hasWritePermission;

  const {
    split_transactions = [],
    transaction,
    has_linked_invoices,
  } = info.row.original;

  const entityId = useCurrentEntityId();
  const transactionId = transaction.uuid;

  let tooltipText = transaction.category?.name || "";

  if (showSplitActions && split_transactions) {
    tooltipText = split_transactions
      .map((t) => t.category?.name)
      .filter(Boolean)
      .join(", ");
  }

  if (has_linked_invoices) {
    return <Invoices transactionId={transactionId} entityId={entityId} />;
  }

  if (showSplitActions && split_transactions) {
    return (
      <SplitCategoryOnPopover
        transaction={info.row.original}
        truncate={truncate}
        showSplitActions={showSplitActions}
        tooltipText={tooltipText}
        key={info.row.original.transaction.uuid}
        isDisabled={effectiveIsDisabled}
      />
    );
  }

  return (
    <CategorySelector
      transaction={info.row.original}
      truncate={truncate}
      showSplitActions={showSplitActions}
      txnsToShowInLabel={tooltipText}
      tooltip
      tooltipText={tooltipText}
      isDisabled={effectiveIsDisabled}
    />
  );
};

const Invoice = (info: CellContext<Transactions, Transactions["invoices"]>) => {
  const invoices = info.getValue();

  return (
    <>
      {invoices.length !== 0 ? (
        <AvatarGroup max={1} variant="no-fill">
          {invoices.map(({ uuid, file_data }) => (
            <Button key={uuid} customType="ghost_icon" size="xsmall">
              <FileIcon fileType={file_data?.file_type} size="24" />
            </Button>
          ))}
        </AvatarGroup>
      ) : (
        <div onClick={(e) => e.stopPropagation()}>
          <AttachInvoice transactionId={info.row.original.transaction.uuid} />
        </div>
      )}
    </>
  );
};

export const TxnAmount = ({
  amount,
  currencyCode,
}: {
  amount: number;
  currencyCode?: string;
}) => {
  const isCredit = amount > 0;

  return (
    <div
      className={classNames(
        "t-flex t-justify-end t-whitespace-nowrap t-text-body-sm",
        {
          "t-text-dark_green-50": isCredit,
        }
      )}
    >
      <AmountSuperScript amount={amount} currencyCode={currencyCode} />
    </div>
  );
};

const Actions = ({
  info,
  onRequestInfo,
  sendToChat,
  onRowClick,
  onDuplicate,
}: {
  info: CellContext<Transactions, Transactions["transaction"]>;
  onRequestInfo?: ({ transactionId }: { transactionId: string }) => void;
  sendToChat?: (uuid: string) => void;
  onRowClick?: (uuid: string, e: Event) => void;
  onDuplicate?: (transaction: Transaction) => void;
}) => {
  const hasWritePermission = useHasWritePermission(
    WRITE_PERMISSION_KEYS.TRANSACTIONS
  );

  const {
    row: {
      original: {
        transaction: { uuid },
      },
    },
  } = info;

  const { Action, modals } = useTransactionActions({
    transactionId: uuid,
    transactionData: info.row.original,
    onRequestInfo,
    sendToChat,
    onRowClick,
    onDuplicate,
  });

  if (!hasWritePermission) {
    return (
      <div
        className="t-pointer-events-none"
        onClick={(e) => e.stopPropagation()}
      >
        <Button size="small" customType="ghost_icon" disabled>
          <Padlock />
        </Button>
      </div>
    );
  }

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <AddCommentPopoverRoot contentId={info.row.original.transaction.uuid}>
        <Dropdown.Root>
          <Dropdown.Trigger asChild>
            <Popover.Anchor asChild>
              <div>
                <Button size="xsmall" customType="ghost_icon">
                  <img src={ThreeDots} alt="Action" className="t-select-none" />
                </Button>
              </div>
            </Popover.Anchor>
          </Dropdown.Trigger>
          <Popover.Portal>
            <AddCommentPop
              commentType={COMMENT_TYPE.TRANSACTION_COMMENT}
              contentId={info.row.original.transaction.uuid}
            />
          </Popover.Portal>
          <Dropdown.Portal>
            <Dropdown.Content
              sideOffset={8}
              side="bottom"
              className="t-mr-16 !t-w-72"
              align="start"
            >
              <ActionDropdown
                Action={Action}
                transactionData={info.row.original}
              />
            </Dropdown.Content>
          </Dropdown.Portal>
        </Dropdown.Root>
      </AddCommentPopoverRoot>
      {modals}
    </div>
  );
};

const DailyEntry = ({ transaction }: { transaction: Transactions }) => {
  const { stripe_aggregate_entry_ledger_entry } = transaction;
  const { link } = useConstructInternalLink();

  if (!stripe_aggregate_entry_ledger_entry) {
    return <div className="t-text-text-60">-</div>;
  }

  const { aggregate_entry_date, name, uuid } =
    stripe_aggregate_entry_ledger_entry;

  return (
    <span className="t-flex t-flex-col t-w-max">
      <p className="t-text-text-30 t-text-caption !t-m-0">Added to</p>
      <ToolTip text={name}>
        <span>
          <Link
            to={link(`/books/journal-entries`, {
              moreQuery: {
                journal_entry_id: uuid,
              },
            })}
            target="_blank"
            onClick={(e) => e.stopPropagation()}
          >
            <Button customType="link">
              JE - {dayjs(aggregate_entry_date).format(DISPLAY_DATE)}
            </Button>
          </Link>
        </span>
      </ToolTip>
    </span>
  );
};

const TransactionColumn = {
  Category,
  Actions,
  Amount: TxnAmount,
  DailyEntry,
  Invoice,
  Merchant,
  Source,
  SourceLogo,
};

export default TransactionColumn;
